import { useEffect, useState } from 'react';

/**
 * Canonical viewport state for JS-side responsive branching.
 * Buckets: mobile < 768 <= tablet < 1024 <= desktop.
 * See .kiro/steering/frontend-details.md "Responsive design system".
 */
export interface ViewportState {
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  /** Raw px width on the client; `null` during SSR and first client render. */
  width: number | null;
}

const MOBILE_MQL = '(max-width: 767px)';
const TABLET_MQL = '(min-width: 768px) and (max-width: 1023px)';

/**
 * SSR-safe viewport hook. Prefer Tailwind responsive classes (`md:hidden`,
 * `hidden md:block`) — reach for this hook only when the mobile and desktop
 * trees are structurally divergent or you need a value (not a style) that
 * depends on the bucket. When used on a first-paint-visible surface, gate
 * rendering on `width !== null` so the wrong tree never flashes.
 */
export function useViewport(): ViewportState {
  const [state, setState] = useState<ViewportState>({
    isMobile: false,
    isTablet: false,
    isDesktop: true,
    width: null,
  });

  useEffect(() => {
    const mqMobile = window.matchMedia(MOBILE_MQL);
    const mqTablet = window.matchMedia(TABLET_MQL);

    const compute = (): ViewportState => {
      const isMobile = mqMobile.matches;
      const isTablet = mqTablet.matches;
      return {
        isMobile,
        isTablet,
        isDesktop: !isMobile && !isTablet,
        width: window.innerWidth,
      };
    };

    setState(compute());

    const onChange = () => setState(compute());
    mqMobile.addEventListener('change', onChange);
    mqTablet.addEventListener('change', onChange);
    return () => {
      mqMobile.removeEventListener('change', onChange);
      mqTablet.removeEventListener('change', onChange);
    };
  }, []);

  return state;
}
